import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";

import { cx } from "@/lib/cx";

/** Semantic data table. Density follows the membership "table_density" preference via data-density. */
export function Table({
  children,
  label,
  density = "comfortable",
  tour,
  className,
}: {
  children: ReactNode;
  label: string;
  density?: "comfortable" | "compact";
  tour?: string;
  className?: string;
}) {
  return (
    <div data-tour={tour} className={cx("overflow-x-auto rounded-lg border border-line bg-surface", className)}>
      <table aria-label={label} data-density={density} className="group/table w-full border-collapse text-sm">
        {children}
      </table>
    </div>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return <thead className="sticky top-0 z-[1] bg-surface">{children}</thead>;
}

export function Th({ className, ...rest }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      scope="col"
      className={cx(
        "border-b border-line px-3 py-2 text-left text-xs font-medium whitespace-nowrap text-fg-subtle",
        className,
      )}
      {...rest}
    />
  );
}

export function Tr({ className, interactive, selected, ...rest }: HTMLAttributes<HTMLTableRowElement> & { interactive?: boolean; selected?: boolean }) {
  return (
    <tr
      className={cx(
        "border-b border-line last:border-b-0",
        interactive && "cursor-pointer transition-colors transition-fast hover:bg-surface-hover focus-within:bg-surface-hover",
        selected && "bg-accent-subtle hover:bg-accent-subtle",
        className,
      )}
      {...rest}
    />
  );
}

export function Td({ className, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td
      className={cx("px-3 py-2.5 align-middle text-fg group-data-[density=compact]/table:py-1.5", className)}
      {...rest}
    />
  );
}
